import { identity, omit } from 'lodash-es';
import { MEDIA_UPLOAD_SERVICE_BASEURL } from 'config';
import ServiceFactory, { returnVoid } from 'services/ServiceFactory';
import { createFormData } from 'utils/formData';
import {
  DIRECT_UPLOAD_PATH,
  ENTIRE_VIDEO_DETAIL_PATH,
  ENTIRE_VIDEO_INSTANCE_DETAIL_PATH,
  ENTIRE_VIDEO_INSTANCE_PATH,
  ENTIRE_VIDEO_SEARCH_PATH,
  GIF_CONVERTER_PATH,
  MEDIA_CONTENT_INFO_PATH,
  MEDIA_SEGMENT_DETAIL_BY_ID_PATH,
  MEDIA_SEGMENT_DETAIL_BY_REVISION_ID_PATH,
  MULTIPART_DIRECT_UPLOAD_ABORT_PATH,
  MULTIPART_DIRECT_UPLOAD_COMPLETE_PATH,
  MULTIPART_DIRECT_UPLOAD_INIT_PATH,
  MULTIPART_DIRECT_UPLOAD_PRESIGN_PARTS_PATH,
  PARSE_EXTERNAL_MEDIA_PATH,
  TEXT_BLOB_DETAIL_PATH,
  TEXT_BLOB_PATH,
  VIDEO_LAYOUT_TIMELINE_DETAIL_PATH,
  VIDEO_LAYOUT_TIMELINE_REVISION_DETAIL_PATH,
  VIDEO_LAYOUT_TIMELINE_REVISION_SEGMENT_PATH,
  VIDEO_UPLOAD_DETAIL_PATH,
  VIDEO_UPLOAD_PATH,
  VIDEO_UPLOAD_SEARCH_PATH,
  YOUTUBE_INFO_PATH,
} from './constants';
import {
  CreateEntireVideoInstanceArgs,
  CreateTextBlobOptions,
  DirectUploadOptions,
  CreateEntireVideoInstanceResult,
  EntireVideo,
  GetEntireVideoByIdArgs,
  GetEntireVideoInstanceByIdArgs,
  GetMediaContentInfoArgs,
  GetMediaSegmentDataByIdArgs,
  GetMediaSegmentDataByRevisionIdArgs,
  GetTextBlobArgs,
  GetVideoLayoutTimelineDetailByIdArgs,
  GetVideoLayoutTimelineDetailByRevisionIdArgs,
  GetVideoUploadArgs,
  GetVideoUploadsOptions,
  GetVideoUploadsResult,
  GetYoutubeVideoInfoArgs,
  GifConversionOptions,
  GifConversionResult,
  MediaContentInfo,
  MediaSegmentData,
  ParseExternalMediaArgs,
  ParsedExternalMediaResult,
  SignedUploadBody,
  TextBlob,
  UpdateVideoLayoutTimelineArgs,
  UpdateVideoLayoutTimelineSegmentArgs,
  UploadVideoOptions,
  UploadVideoResult,
  VideoLayoutTimeline,
  VideoLayoutTimelineId,
  VideoUpload,
  VideoUploadSearchOptions,
  VideoUploadSearchResult,
  YoutubeInfoResult,
  GetEntireVideoInstanceByIdResult,
  EntireVideoSearchOptions,
  EntireVideoSearchResult,
  InitMultipartDirectUploadOptions,
  InitMultipartDirectUploadResult,
  PreSignMultipartDirectUploadPartsOptions,
  PreSignMultipartDirectUploadPartsResult,
  CompleteMultipartDirectUploadOptions,
  CompleteMultipartDirectUploadResult,
  AbortMultipartDirectUploadOptions,
} from './types';

const serviceFactory = new ServiceFactory(MEDIA_UPLOAD_SERVICE_BASEURL);

/**
 * Retrieve YouTube Video Information
 *
 * Takes YouTube url and returns various information about the YouTube video.
 */
export const getYoutubeVideoInfo = serviceFactory.createGetEndpoint(
  YOUTUBE_INFO_PATH,
)<GetYoutubeVideoInfoArgs, YoutubeInfoResult>({
  queryParams: identity,
});

/**
 * Retrieves video upload entries
 */
export const getVideoUploads = serviceFactory.createGetEndpoint(
  `${VIDEO_UPLOAD_PATH}/`,
)<GetVideoUploadsOptions, GetVideoUploadsResult>({
  queryParams: identity,
});

/**
 * Retrieves video upload entry
 */
export const getVideoUploadDetail = serviceFactory.createGetEndpoint(
  VIDEO_UPLOAD_DETAIL_PATH,
)<GetVideoUploadArgs, VideoUpload>();

/**
 * Creates request to upload video to SpareMin
 */
export const uploadVideo = serviceFactory.createPostEndpoint(
  `${VIDEO_UPLOAD_PATH}/`,
)({
  data: (args: UploadVideoOptions) => createFormData(args),
  select: identity<UploadVideoResult>,
});

/**
 * Retrieves video upload entry with matching fields if it exists
 *
 * Expects at least one video source to be provided. If user is using
 * a local video source, the api caller must calculate the md5 of the video
 * file and provide it in the md5 field.
 */
export const searchVideoUploads = serviceFactory.createGetEndpoint(
  // NB: the trailing slash is necessary here.  see https://stash.sparemin.com/projects/SPAR/repos/embed-generator-app/pull-requests/4770/overview
  `${VIDEO_UPLOAD_SEARCH_PATH}/`,
)<VideoUploadSearchOptions, VideoUploadSearchResult>({
  queryParams: identity,
});

/**
 * Create a text blob entry
 *
 * This end point will also make the request to create a transcript
 * for this text blob and kick off the image recommendation background
 * job. For clients that want to utilize the image recommendation,
 * use the keyword-extractor-service’s endpoint for retrieving
 * analysis by text blob id
 */
export const createTextBlob = serviceFactory.createPostEndpoint(
  `${TEXT_BLOB_PATH}/`,
)({
  data: (args: CreateTextBlobOptions) => createFormData(args),
  select: identity<TextBlob>,
});

/**
 * Retrieves text blob entry
 */
export const getTextBlobDetail = serviceFactory.createGetEndpoint(
  TEXT_BLOB_DETAIL_PATH,
)<GetTextBlobArgs, TextBlob>();

/**
 * Creates request to convert a gif to a video
 */
export const convertGifToVideo = serviceFactory.createPostEndpoint(
  `${GIF_CONVERTER_PATH}/`,
)({
  data: (args: GifConversionOptions) => createFormData(args),
  select: identity<GifConversionResult>,
});

/**
 * Parse external Content
 */
export const parseExternalMedia = serviceFactory.createGetEndpoint(
  PARSE_EXTERNAL_MEDIA_PATH,
)({
  queryParams: identity<ParseExternalMediaArgs>,
  select: identity<ParsedExternalMediaResult>,
});

/**
 * Request For Signed Direct Upload URL
 */
export const directUpload = serviceFactory.createPostEndpoint(
  `${DIRECT_UPLOAD_PATH}/`,
)({
  data: (args: DirectUploadOptions) => createFormData(args),
  select: identity<SignedUploadBody>,
});

/**
 * Initiate a multipart upload. This is step 1 of the direct-upload multipart flow.
 *
 * Flow:
 * (1) Init (this endpoint)
 * (2) POST .../multipart/presign-parts to be given presigned PUT URLs for the given part numbers
 * (3) PUT each part to its presigned URL (part numbers are 1-based)
 * (4) POST .../multipart/complete with part numbers and etags (from s3 response header), or POST
 *     .../multipart/abort to cancel.
 *
 * After step 3, read the ETag from each part's S3 PUT response header and send those in the
 * complete request. On upload failure, retry only the failed part(s) by part number; then call
 * complete with all part etags. Use partSizeBytes from this response to split the file (e.g. 20 MB
 * per part; last part may be smaller).
 */
export const initMultipartDirectUpload = serviceFactory.createPostEndpoint(
  MULTIPART_DIRECT_UPLOAD_INIT_PATH,
)({
  data: (args: InitMultipartDirectUploadOptions) => createFormData(args),
  select: identity<InitMultipartDirectUploadResult>,
});

/**
 * Get presigned PUT URLs for the given part numbers.
 */
export const preSignMultipartDirectUploadParts =
  serviceFactory.createPostEndpoint(MULTIPART_DIRECT_UPLOAD_PRESIGN_PARTS_PATH)(
    {
      data: identity<PreSignMultipartDirectUploadPartsOptions>,
      select: identity<PreSignMultipartDirectUploadPartsResult>,
    },
  );

/**
 * Complete the multipart upload with the given part ETags.
 */
export const completeMultipartDirectUpload = serviceFactory.createPostEndpoint(
  MULTIPART_DIRECT_UPLOAD_COMPLETE_PATH,
)({
  data: identity<CompleteMultipartDirectUploadOptions>,
  select: identity<CompleteMultipartDirectUploadResult>,
});

/**
 * Abort an incomplete multipart upload.
 */
export const abortMultipartDirectUpload = serviceFactory.createPostEndpoint(
  MULTIPART_DIRECT_UPLOAD_ABORT_PATH,
)({
  data: identity<AbortMultipartDirectUploadOptions>,
  select: returnVoid,
});

/**
 * Creates an entire video instance from a video source.
 */
export const createEntireVideoInstance = serviceFactory.createPostEndpoint(
  ENTIRE_VIDEO_INSTANCE_PATH,
)({
  queryParams: ['uploadedVideoId'],
  data: (args: CreateEntireVideoInstanceArgs) =>
    createFormData(omit(args, 'uploadedVideoId')),
  select: identity<CreateEntireVideoInstanceResult>,
});

/**
 * Retrieves entire video instance by ID.
 */
export const getEntireVideoInstanceById = serviceFactory.createGetEndpoint(
  ENTIRE_VIDEO_INSTANCE_DETAIL_PATH,
)<GetEntireVideoInstanceByIdArgs, GetEntireVideoInstanceByIdResult>();

/**
 * Retrieves entire video by ID.
 */
export const getEntireVideoById = serviceFactory.createGetEndpoint(
  ENTIRE_VIDEO_DETAIL_PATH,
)<GetEntireVideoByIdArgs, EntireVideo>();

/**
 * Search for an existing entire video entry.
 */
export const searchEntireVideo = serviceFactory.createGetEndpoint(
  ENTIRE_VIDEO_SEARCH_PATH,
)<EntireVideoSearchOptions, EntireVideoSearchResult>({
  queryParams: identity,
});

/**
 * Retrieve External Media Content Info
 *
 * Takes image url (static image or animated) and returns the contentType
 * and animation info. This is mainly used in the make app to detect
 * animated gif vs static image.
 */
export const getMediaContentInfo = serviceFactory.createGetEndpoint(
  MEDIA_CONTENT_INFO_PATH,
)({
  queryParams: identity<GetMediaContentInfoArgs>,
  select: identity<MediaContentInfo>,
});

/**
 * Retrieve Media Segment Data by Revision ID
 */
export const getMediaSegmentDetailByRevisionId =
  serviceFactory.createGetEndpoint(MEDIA_SEGMENT_DETAIL_BY_REVISION_ID_PATH)<
    GetMediaSegmentDataByRevisionIdArgs,
    MediaSegmentData
  >();

/**
 * Retrieve Media Segment Data by UUID
 */
export const getMediaSegmentDetailById = serviceFactory.createGetEndpoint(
  MEDIA_SEGMENT_DETAIL_BY_ID_PATH,
)<GetMediaSegmentDataByIdArgs, MediaSegmentData>();

/**
 * Retrieve Video Layout Timeline Data by UUID
 */
export const getVideoLayoutTimelineDetailById =
  serviceFactory.createGetEndpoint(VIDEO_LAYOUT_TIMELINE_DETAIL_PATH)<
    GetVideoLayoutTimelineDetailByIdArgs,
    VideoLayoutTimeline
  >({
    queryParams: ['includeDynamicCropData', 'includeSubjects'],
  });

/**
 * Retrieve Video Layout Timeline Data by Revision ID
 */
export const getVideoLayoutTimelineDetailByRevisionId =
  serviceFactory.createGetEndpoint(VIDEO_LAYOUT_TIMELINE_REVISION_DETAIL_PATH)<
    GetVideoLayoutTimelineDetailByRevisionIdArgs,
    VideoLayoutTimeline
  >({
    queryParams: ['includeDynamicCropData', 'includeSubjects'],
  });

/**
 * Should be used when times are changed, multiple segments need to be updated,
 * segments are added/deleted, etc.
 */
export const updateVideoLayoutTimeline = serviceFactory.createPutEndpoint(
  VIDEO_LAYOUT_TIMELINE_REVISION_DETAIL_PATH,
)({
  data: (args: UpdateVideoLayoutTimelineArgs) =>
    omit(args, 'uuid', 'revisionId'),
  select: identity<VideoLayoutTimelineId>,
});

/**
 * Only one segment will be updated. Expected to be used when only slots are updated.
 * Times will not be updated.
 */
export const updateVideoLayoutTimelineSegment =
  serviceFactory.createPutEndpoint(VIDEO_LAYOUT_TIMELINE_REVISION_SEGMENT_PATH)(
    {
      data: (args: UpdateVideoLayoutTimelineSegmentArgs) =>
        omit(args, 'uuid', 'revisionId', 'segmentId'),
      select: identity<VideoLayoutTimelineId>,
    },
  );
